import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  DeleteSweep as ClearIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { AppIconButton } from '../components/ui';
import api from '../api/api';

interface ChatMessage {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  message: string;
  timestamp: string;
  status?: string;
  type?: string;
}

/** Render message text with clickable links */
const Linkify: React.FC<{ text: string }> = ({ text }) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return (
    <>
      {parts.map((part, i) =>
        urlRegex.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#039be5', wordBreak: 'break-all' }} /* basil-todo */
          >
            {part}
          </a>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

const QUICK_ACTIONS = [
  { label: 'QR Order', message: "Hi! I'd like to order. QR: d261ab9c" },
  { label: 'Hi', message: 'Hi' },
  { label: 'Menu', message: 'menu' },
  { label: 'Status', message: 'status' },
  { label: 'Help', message: 'help' },
  { label: 'Cancel', message: 'cancel' },
];

const WhatsAppSimulator: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const phone = searchParams.get('phone') || '+919876543210';
  const [messageInput, setMessageInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState<boolean | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sseConnected = useRef(false);

  // Check mock endpoint on mount
  useEffect(() => {
    api.get('/api/test/whatsapp/simulate/mock/messages')
      .then(() => setConnected(true))
      .catch(() => setConnected(false));
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // SSE stream for real-time push notifications (order confirmations, status updates)
  useEffect(() => {
    if (!connected) return;
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const eventSource = new EventSource(
      `${baseUrl}/api/test/whatsapp/simulate/stream?phone=${encodeURIComponent(phone)}`
    );

    eventSource.addEventListener('connected', () => {
      sseConnected.current = true;
    });

    eventSource.addEventListener('whatsapp-message', (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'read_receipt') return;

      setMessages(prev => [...prev, {
        id: `sse_${Date.now()}_${Math.random()}`,
        direction: 'OUTBOUND',
        message: data.body,
        timestamp: data.timestamp,
        type: data.type,
      }]);
    });

    eventSource.onerror = () => { /* EventSource reconnects automatically */ };
    return () => {
      sseConnected.current = false;
      eventSource.close();
    };
  }, [connected, phone]);

  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    const inboundMsg: ChatMessage = {
      id: `in_${Date.now()}`,
      direction: 'INBOUND',
      message: trimmed,
      timestamp: new Date().toISOString(),
      status: 'sending',
    };
    setMessages(prev => [...prev, inboundMsg]);
    setMessageInput('');
    setLoading(true);

    try {
      const res = await api.post('/api/test/whatsapp/simulate/inbound', {
        phone,
        message: trimmed,
      });

      // Update inbound status
      setMessages(prev =>
        prev.map(m => m.id === inboundMsg.id ? { ...m, status: res.data.status } : m)
      );

      // When SSE is connected, it delivers all outbound messages — skip inline to avoid duplicates
      if (!sseConnected.current) {
        const responses: ChatMessage[] = (res.data.responses || []).map(
          (r: { body?: string; message?: string; type?: string; timestamp?: string }, i: number) => ({
            id: `out_${Date.now()}_${i}`,
            direction: 'OUTBOUND' as const,
            message: r.body || r.message || '',
            timestamp: r.timestamp || new Date().toISOString(),
            type: r.type,
          })
        );
        if (responses.length > 0) {
          setMessages(prev => [...prev, ...responses]);
        }
      }
    } catch (err: unknown) {
      setMessages(prev =>
        prev.map(m => m.id === inboundMsg.id ? { ...m, status: 'FAILED' } : m)
      );
      const errorMsg = err instanceof Error ? err.message : 'Send failed';
      setMessages(prev => [...prev, {
        id: `err_${Date.now()}`,
        direction: 'OUTBOUND',
        message: `Error: ${errorMsg}`,
        timestamp: new Date().toISOString(),
        type: 'error',
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [phone, loading]);

  const clearChat = async () => {
    try {
      await api.delete('/api/test/whatsapp/simulate/mock/messages');
    } catch {
      // ignore
    }
    setMessages([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(messageInput);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#e5ddd5' }}> {/* basil-todo */}
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#075e54' }}> {/* basil-todo */}
        <Toolbar sx={{ minHeight: 56 }}>
          <AppIconButton
            icon={<ArrowBackIcon />}
            tooltip="Go back"
            variant="ghost"
            edge="start"
            onClick={() => navigate(-1)}
            sx={{ mr: 1, color: 'inherit', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
              WhatsApp Simulator
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.7rem' }}>
              {phone}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: connected === true ? '#4caf50' : connected === false ? '#f44336' : '#9e9e9e',
              }}
            />
            <AppIconButton
              icon={<ClearIcon />}
              tooltip="Clear conversation"
              variant="ghost"
              size="small"
              onClick={clearChat}
              sx={{ color: 'inherit', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Chat area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 1.5,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 4, color: '#888' }}> {/* basil-todo */}
            <Typography variant="body2">
              Send a QR code to start a session (e.g. "QR: d261ab9c" or just the code)
            </Typography>
          </Box>
        )}
        {messages.map(msg => (
          <Box
            key={msg.id}
            sx={{
              display: 'flex',
              justifyContent: msg.direction === 'INBOUND' ? 'flex-end' : 'flex-start',
              mb: 0.25,
            }}
          >
            <Paper
              elevation={1}
              sx={{
                maxWidth: '80%',
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
                bgcolor: msg.type === 'error'
                  ? '#ffcdd2'
                  : msg.direction === 'INBOUND'
                    ? '#dcf8c6'
                    : '#ffffff',
                borderTopRightRadius: msg.direction === 'INBOUND' ? 0 : undefined,
                borderTopLeftRadius: msg.direction === 'OUTBOUND' ? 0 : undefined,
              }}
            >
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}
              >
                <Linkify text={msg.message} />
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: 'block', textAlign: 'right', color: 'var(--text-tertiary)', mt: 0.25, fontSize: '0.65rem' }}
              >
                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {msg.status === 'FAILED' && ' - Failed'}
              </Typography>
            </Paper>
          </Box>
        ))}
        <div ref={chatEndRef} />
      </Box>

      {/* Quick actions */}
      <Box sx={{ px: 1.5, py: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap', bgcolor: 'var(--bg-sunken)' }}>
        {QUICK_ACTIONS.map(action => (
          <Chip
            key={action.label}
            label={action.label}
            size="small"
            onClick={() => sendMessage(action.message)}
            disabled={loading}
            sx={{
              bgcolor: 'var(--bg-surface)',
              '&:hover': { bgcolor: '#e8e8e8' }, /* basil-todo */
              fontSize: '0.75rem',
            }}
          />
        ))}
      </Box>

      {/* Input area */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, p: 1, bgcolor: 'var(--bg-sunken)' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type a message..."
          value={messageInput}
          onChange={e => setMessageInput(e.target.value)}
          onKeyDown={handleKeyDown}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 4,
              bgcolor: 'var(--bg-surface)',
            },
          }}
        />
        <AppIconButton
          icon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" />}
          tooltip="Send message"
          variant="primary"
          onClick={() => sendMessage(messageInput)}
          disabled={loading || !messageInput.trim()}
          sx={{
            width: 40,
            height: 40,
          }}
        />
      </Box>
    </Box>
  );
};

export default WhatsAppSimulator;
